import {
  getCampaignKeys,
  getCampaignParameters,
} from '@/components/molecules/CampaignManager'
import { buildSearchParamsString } from '@/utils/buildSearchParamsString'

/**
 * Resolves the search params used to seed the donation form `defaultValues`.
 *
 * Two flows share the `<Form>`:
 *
 * - Legacy `/sostienici` and `/lnd` (no `etsDefaults`): the caller already resolved
 *   the params upstream, so they are used as-is. This branch is intentionally
 *   untouched to keep the legacy behavior identical.
 *
 * - Dynamic campaign routes (`etsDefaults` provided): the 4 ETS keys are
 *   resolved through `getCampaignParameters` with the existing precedence
 *   (query ETS > cookie > `etsDefaults`). Every *other* query param (e.g.
 *   `donationAmount`, `firstName`) is merged in so it prefills the form like
 *   the legacy flow, fixing marketing links that rely on query prefill (M-2).
 *
 *   ETS keys are stripped from the non-ETS slice on purpose: they must only be
 *   honored through `getCampaignParameters`. A landing link may carry only some
 *   ETS keys (typically `ets_cmmk` and `ets_sgmt`); those sit on top of cookie
 *   and `etsDefaults`, and the missing keys fall through. Since ETS and
 *   non-ETS keys are disjoint, the resolved ETS params always win in the merge.
 *
 * @param {Record<string, string | string[] | undefined>} [searchParams]
 * @param {object} [etsDefaults] campaign ETS defaults; absent/undefined ⇒ legacy
 * @param {string} cookieName campaign cookie name
 * @returns {Record<string, unknown>} params to spread into form `defaultValues`
 */
export function resolveFormSearchParams(searchParams, etsDefaults, cookieName) {
  if (!etsDefaults) return searchParams

  const campaignParams = getCampaignParameters(
    cookieName,
    etsDefaults,
    buildSearchParamsString(searchParams)
  )

  const etsKeys = new Set(getCampaignKeys())
  const nonEtsSearchParams = {}
  for (const [key, value] of Object.entries(searchParams ?? {})) {
    if (!etsKeys.has(key)) nonEtsSearchParams[key] = value
  }

  return { ...nonEtsSearchParams, ...campaignParams }
}

export default resolveFormSearchParams
